import React, { useState, useMemo, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import Avatar from 'components/Avatar';
import CommentEditor from 'components/Discussion/CommentEditor';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APResponsiveDropdown from 'components/Dropdown/APResponsiveDropdown';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { imagePickSize } from 'utils/general';
import { getTimezoneShort } from 'utils/time';
import { useSelector } from 'react-redux';

const LeadRecordActivity = ({
  comments: unsortedComments = [],
  timeZone,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  handleActivityClick,
}) => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const [newComment, setNewComment] = useState('');
  const comments = useMemo(() => {
    const toSort = [...unsortedComments];
    return toSort.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)));
  }, [unsortedComments]);
  // Need to check that the comment was successfully added so we know to clear the editor.
  const commentCount = useRef(comments.length);

  const addComment = () => {
    onAddComment(newComment);
  };

  useEffect(() => {
    // New comment was added.
    if (comments.length > commentCount.current) {
      setNewComment('');
      commentCount.current = comments.length;
    }
  }, [comments.length]);

  return (
    <div className="lead-activity-log">
      <label
        className="comment-editor-label"
        htmlFor="comment-editor"
      >
        Log an Activity
      </label>
      <CommentEditor
        name="comment"
        value={newComment}
        onChange={(e) => setNewComment(e.target.value)}
        actions={[{ onClick: addComment, title: 'Save' }]}
        rows={1}
        textareaProps={{ id: 'comment-editor' }}
      />
      {comments.map((comment) => {
        return (
          <ActivityItem
            key={`lead-comment-${comment.id}`}
            login={userInfo}
            comment={comment}
            timeZone={timeZone}
            onUpdate={onUpdateComment}
            onDelete={onDeleteComment}
            handleActivityClick={handleActivityClick}
          />
        );
      })}
    </div>
  );
};

const ActivityItem = ({
  login,
  comment,
  timeZone,
  onDelete,
  onUpdate,
  handleActivityClick,
}) => {
  const { id: loginId } = login || {};
  const { profileImage } = comment.login || {};
  const { updatedAt, createdAt, kind, clientEmail } = comment || {};
  const { threadId } = clientEmail || {};
  const isCommentOwner = loginId === comment.loginId;
  const isAbleToEdit = isCommentOwner && kind === 'user';
  const isEmailComment = !!threadId;
  // Add additional cases here if other conditions make the log clickable.
  const isClickable = isEmailComment;
  const isUpdated = !!updatedAt;
  const timestamp = isUpdated ? updatedAt : createdAt;
  const formattedDate = dayjs(timestamp)
    .tz(timeZone)
    .format('MMM DD, YYYY h:mmA');
  const timezoneShort = getTimezoneShort(timeZone);

  const imagePath = useMemo(() => {
    if (profileImage) {
      return `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(
        profileImage,
        'square60',
      )}`;
    }
  }, [profileImage]);

  const [isEditing, setIsEditing] = useState(false);
  const [commentBody, setCommentBody] = useState(comment.body || '');

  const handleClick = () => {
    if (!isClickable) return;
    if (threadId) {
      handleActivityClick('email', { threadId });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleUpdate = () => {
    onUpdate(comment.id, commentBody);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setCommentBody(comment.body);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(comment.id);
  };

  return (
    <div
      key={comment.id}
      className={clsx('lead-activity', isClickable && 'lead-activity--clickable')}
      onClick={handleClick}
    >
      <div className="lead-activity__img">
        <Avatar
          photo={imagePath}
          text={profileImage ? '' : comment.login.initials}
          size={32}
          borderSize={2}
          className="avatar--transparent avatar--dark"
        />
      </div>
      <div className="lead-activity__content">
        <div className="lead-activity__head">
          <div>
            <h6 className="lead-activity__name">
              {comment.login.firstname} {comment.login.lastname}
            </h6>
          </div>
          <div className="lead-activity__date">
            {`${formattedDate} ${timezoneShort}`}
            {isUpdated && (
              <>&nbsp;(edited)</>
            )}
          </div>
        </div>
        {isEditing ? (
          <CommentEditor
            name="edit-comment"
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            actions={[
              { onClick: handleCancel, title: 'Cancel', className: 'button--cancel' },
              { onClick: handleUpdate, title: 'Save' },
            ]}
            rows={1}
          />
        ) : (
          <p className="lead-activity__text">{commentBody}</p>
        )}
      </div>
      {isAbleToEdit && (
        <ActivityLogOptions
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )
        /* will be implemented later
        <div className="lead-activity__status">New</div>
        */
      }
    </div>
  );
};

const ActivityLogOptions = ({
  onEdit,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);

  const show = () => setIsOpen(true);
  const dismiss = () => setIsOpen(false);

  const handleEdit = () => {
    dismiss();
    onEdit();
  };

  const handleDelete = () => {
    dismiss();
    setDeleteConfirmOpened(true);
  };

  const trigger = (
    <DropdownButton className="button--unstyled button--options">
      <span className="fa fa-ellipsis-v" />
    </DropdownButton>
  );

  return (
    <div className="lead-activity__options">
      <APResponsiveDropdown
        opened={isOpen}
        show={show}
        dismiss={dismiss}
        triggerButton={trigger}
        placement="bottom-end"
        menuClass="comment-options-dropdown"
      >
        <>
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={handleEdit}
          >
            Edit
          </button>
          <button
            className={clsx(
              'button--unstyled',
              'button--menu',
            )}
            onClick={handleDelete}
          >
            Delete
          </button>
        </>
      </APResponsiveDropdown>
      {deleteConfirmOpened && (
        <DeleteConfirmation
          show
          handleClose={() => {
            setDeleteConfirmOpened(false);
          }}
          handleConfirm={onDelete}
          deleteText="Delete"
          size="xs"
        >
          <p>
            Deleting this comment will remove it permanently from this conversation.
          </p>
        </DeleteConfirmation>
      )}
    </div>
  );
};

export default LeadRecordActivity;
